import { Chantier, ChantierOrder, ChantierQuote, ChantierOrderItem, ChantierQuoteItem, StockMovement } from '../types';

// Mock data
const mockChantiers: Chantier[] = [
  {
    id: 'chantier-1',
    code: 'CH-2024-001',
    name: 'Maison Dupont',
    chefEquipeId: '3',
    status: 'en_cours',
    description: 'Construction maison individuelle',
    address: '123 Rue de la Paix, 75001 Paris',
    startDate: '2024-12-01T00:00:00Z',
    endDate: '2025-03-01T00:00:00Z',
    createdAt: '2024-11-15T00:00:00Z',
    updatedAt: '2024-12-01T00:00:00Z'
  },
  {
    id: 'chantier-2',
    code: 'CH-2024-002',
    name: 'Extension Martin',
    chefEquipeId: '3',
    status: 'en_cours',
    description: 'Extension de maison',
    address: '456 Avenue des Chênes, 69000 Lyon',
    startDate: '2024-12-10T00:00:00Z',
    createdAt: '2024-11-20T00:00:00Z',
    updatedAt: '2024-12-10T00:00:00Z'
  },
  {
    id: 'chantier-3',
    code: 'CH-2024-003',
    name: 'Rénovation Bureau',
    chefEquipeId: '4',
    status: 'termine',
    description: 'Rénovation bureaux',
    address: '789 Boulevard du Commerce, 33000 Bordeaux',
    startDate: '2024-10-01T00:00:00Z',
    endDate: '2024-11-30T00:00:00Z',
    createdAt: '2024-09-15T00:00:00Z',
    updatedAt: '2024-11-30T00:00:00Z'
  }
];

const mockChantierOrders: ChantierOrder[] = [
  {
    id: 'order-1',
    chantierId: 'chantier-1',
    status: 'a_preparer',
    items: [
      {
        id: 'item-1',
        productId: 'prod-1',
        quantity: 50,
        preparedQuantity: 0,
        unitPrice: 0.15,
        totalPrice: 7.50,
        comment: 'Pour ossature'
      },
      {
        id: 'item-2',
        productId: 'prod-2',
        quantity: 10,
        preparedQuantity: 0,
        unitPrice: 25.50,
        totalPrice: 255.00
      }
    ],
    totalAmount: 262.50,
    createdBy: '4',
    notes: 'Urgent pour lundi matin',
    createdAt: '2024-12-15T10:00:00Z',
    updatedAt: '2024-12-15T10:00:00Z'
  }
];

const mockChantierQuotes: ChantierQuote[] = [
  {
    id: 'quote-1',
    chantierId: 'chantier-2',
    status: 'brouillon',
    items: [
      {
        productId: 'prod-2',
        quantity: 15,
        unitPrice: 25.50,
        totalPrice: 382.50,
        comment: 'Planches pour structure'
      }
    ],
    totalAmount: 382.50,
    validUntil: '2024-12-31T00:00:00Z',
    reserveStock: false,
    createdBy: '3',
    createdAt: '2024-12-15T14:00:00Z'
  }
];

class ChantierService {
  // Chantiers
  async getChantiers(filters?: {
    status?: string;
    chefEquipeId?: string;
  }): Promise<Chantier[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    let filtered = [...mockChantiers];
    
    if (filters?.status) {
      filtered = filtered.filter(c => c.status === filters.status);
    }
    
    if (filters?.chefEquipeId) {
      filtered = filtered.filter(c => c.chefEquipeId === filters.chefEquipeId);
    }
    
    return filtered.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  async getChantier(id: string): Promise<Chantier | null> {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockChantiers.find(c => c.id === id) || null;
  }

  async createChantier(chantier: Omit<Chantier, 'id' | 'createdAt' | 'updatedAt'>): Promise<Chantier> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newChantier: Chantier = {
      ...chantier,
      id: `chantier-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    mockChantiers.push(newChantier);
    return newChantier;
  }

  async updateChantier(id: string, updates: Partial<Chantier>): Promise<Chantier> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const index = mockChantiers.findIndex(c => c.id === id);
    if (index === -1) throw new Error('Chantier non trouvé');
    
    mockChantiers[index] = {
      ...mockChantiers[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    return mockChantiers[index];
  }

  // Orders
  async getChantierOrders(chantierId?: string): Promise<ChantierOrder[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    let filtered = [...mockChantierOrders];
    
    if (chantierId) {
      filtered = filtered.filter(o => o.chantierId === chantierId);
    }
    
    return filtered.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  async getChantierOrder(id: string): Promise<ChantierOrder | null> {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockChantierOrders.find(o => o.id === id) || null;
  }

  async createChantierOrder(order: Omit<ChantierOrder, 'id' | 'createdAt' | 'updatedAt'>): Promise<ChantierOrder> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newOrder: ChantierOrder = {
      ...order,
      id: `order-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    mockChantierOrders.push(newOrder);
    return newOrder;
  }

  async updateChantierOrder(id: string, updates: Partial<ChantierOrder>): Promise<ChantierOrder> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const index = mockChantierOrders.findIndex(o => o.id === id);
    if (index === -1) throw new Error('Commande non trouvée');
    
    mockChantierOrders[index] = {
      ...mockChantierOrders[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    return mockChantierOrders[index];
  }

  async validateChantierOrder(orderId: string, userId: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const order = mockChantierOrders.find(o => o.id === orderId);
    if (!order) throw new Error('Commande non trouvée');
    
    // Générer les mouvements de sortie
    order.items.forEach(item => {
      const movement: StockMovement = {
        id: `mov-${Date.now()}-${item.id}`,
        productId: item.productId,
        type: 'exit',
        quantity: -item.preparedQuantity,
        reason: `Sortie chantier ${order.chantierId}`,
        userId,
        createdAt: new Date().toISOString()
      };
      // En réalité, cela serait ajouté au service de stock
    });
    
    order.status = 'sortie_validee';
    order.validatedBy = userId;
    order.updatedAt = new Date().toISOString();
  }

  // Quotes
  async getChantierQuotes(chantierId?: string): Promise<ChantierQuote[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    let filtered = [...mockChantierQuotes];
    
    if (chantierId) {
      filtered = filtered.filter(q => q.chantierId === chantierId);
    }
    
    return filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  async createChantierQuote(quote: Omit<ChantierQuote, 'id' | 'createdAt'>): Promise<ChantierQuote> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newQuote: ChantierQuote = {
      ...quote,
      id: `quote-${Date.now()}`,
      createdAt: new Date().toISOString()
    };
    
    mockChantierQuotes.push(newQuote);
    return newQuote;
  }

  async convertQuoteToOrder(quoteId: string): Promise<ChantierOrder> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const quote = mockChantierQuotes.find(q => q.id === quoteId);
    if (!quote) throw new Error('Devis non trouvé');
    
    const orderItems: ChantierOrderItem[] = quote.items.map((item, index) => ({
      id: `item-${Date.now()}-${index}`,
      ...item,
      preparedQuantity: 0
    }));
    
    const newOrder: ChantierOrder = {
      id: `order-${Date.now()}`,
      chantierId: quote.chantierId,
      status: 'panier',
      items: orderItems,
      totalAmount: quote.totalAmount,
      createdBy: quote.createdBy,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    mockChantierOrders.push(newOrder);
    
    // Marquer le devis comme accepté
    quote.status = 'accepte';
    
    return newOrder;
  }

  // Helper methods
  getChantierName(chantierId: string): string {
    const chantier = mockChantiers.find(c => c.id === chantierId);
    return chantier ? `${chantier.code} - ${chantier.name}` : 'Chantier inconnu';
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      // Chantier status
      en_cours: 'En cours',
      suspendu: 'Suspendu',
      termine: 'Terminé',
      annule: 'Annulé',
      // Order status
      panier: 'Panier',
      a_preparer: 'À préparer',
      prete: 'Prête',
      sortie_validee: 'Sortie validée',
      // Quote status
      brouillon: 'Brouillon',
      envoye: 'Envoyé',
      accepte: 'Accepté',
      refuse: 'Refusé'
    };
    return labels[status] || status;
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      // Chantier status
      en_cours: 'bg-green-100 text-green-800',
      suspendu: 'bg-yellow-100 text-yellow-800',
      termine: 'bg-gray-100 text-gray-800',
      annule: 'bg-red-100 text-red-800',
      // Order status
      panier: 'bg-blue-100 text-blue-800',
      a_preparer: 'bg-orange-100 text-orange-800',
      prete: 'bg-green-100 text-green-800',
      sortie_validee: 'bg-gray-100 text-gray-800',
      // Quote status
      brouillon: 'bg-gray-100 text-gray-800',
      envoye: 'bg-blue-100 text-blue-800',
      accepte: 'bg-green-100 text-green-800',
      refuse: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }

  canUserAccessChantier(userId: string, userRole: string, chantier: Chantier): boolean {
    // Admin et responsables peuvent accéder à tous les chantiers
    if (['admin', 'responsable_bureau', 'responsable_depot'].includes(userRole)) {
      return true;
    }
    
    // Chef d'équipe peut accéder à ses chantiers
    if (userRole === 'responsable_equipe' && chantier.chefEquipeId === userId) {
      return true;
    }
    
    // Charpentier peut accéder aux chantiers de son équipe (à implémenter avec teamId)
    if (userRole === 'charpentier') {
      // TODO: Vérifier si l'utilisateur fait partie de l'équipe du chantier
      return true; // Temporaire
    }
    
    return false;
  }
}

export const chantierService = new ChantierService();